import { faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef } from 'react';

import type { User } from '@feathr/blackbox';
import {
  ButtonValid,
  CardV2 as Card,
  Fieldset,
  ImagePicker,
  Input,
  Text,
  Time,
  Tooltip,
  Well,
} from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import TimezoneSelect from '@feathr/extender/components/TimezoneSelect';
import { flattenError, flattenErrors, TimeFormat } from '@feathr/hooks';
import { DefaultLanguage } from '@feathr/locales';

import LanguageSelect from './LanguageSelect';
import { useProfilePage } from './ProfilePage.hooks';

function ProfilePage(): JSX.Element {
  const {
    user,
    t,
    flags,
    errors,
    handleLanguageChange,
    handleSave,
    setLeftActions,
    setRightActions,
    isDirty,
  } = useProfilePage();

  const theme = useMantineTheme();
  const hasUnsavedChanges = isDirty;
  const hasErrors = flattenErrors(errors).length > 0;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    setLeftActions(
      user.get('date_last_modified') ? (
        <Flex gap={theme.spacing['1']}>
          <Text.DefaultSemibold>{t('Last updated')}:</Text.DefaultSemibold>
          <Time
            format={TimeFormat.timeFromNow}
            formatLocal={true}
            showTimezone={true}
            timestamp={user.get('date_last_modified')}
          />
        </Flex>
      ) : undefined,
    );
    setRightActions(
      <ButtonValid
        disabled={hasUnsavedChanges ? undefined : true}
        errors={flattenErrors(errors)}
        onClick={(): Promise<void> => handleSaveRef.current()}
        prefix={<FontAwesomeIcon icon={faSave} />}
      >
        {t('Save changes')}
      </ButtonValid>,
    );
  }, [hasUnsavedChanges, hasErrors]);

  return (
    <Page
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t('Change your profile information, profile picture, etc.')}
      includeCurrentPageCrumb={true}
      showHeader={true}
      title={t('Profile')}
    >
      <Card name={'profile-card'}>
        <Card.Header
          description={t('Your name, profile picture, and email address.')}
          title={t('Profile')}
        />
        <Card.Content addVerticalGap={true}>
          <ImagePicker<User>
            attribute={'picture'}
            buttonText={
              user.get('picture') ? t('Update profile picture') : t('Upload profile picture')
            }
            model={user}
            validationError={flattenError(errors.picture)}
            variant={'rounded'}
          />
          <Fieldset>
            <Input
              attribute={'first_name'}
              label={t('First name')}
              model={user}
              required={true}
              type={'text'}
              validationError={flattenError(errors.first_name)}
            />
            <Input
              attribute={'last_name'}
              label={t('Last name')}
              model={user}
              required={true}
              type={'text'}
              validationError={flattenError(errors.last_name)}
            />
          </Fieldset>
          <Well description={user.get('email')} label={t('Primary email')} name={'account-info'} />
        </Card.Content>
      </Card>

      <Card name={'preferences-card'}>
        <Card.Header
          description={t('Language, timezone, and booking settings.')}
          title={t('Preferences')}
        />
        <Card.Content addVerticalGap={true}>
          <Fieldset direction={'column'}>
            {flags.locales && (
              <LanguageSelect
                label={t('Language')}
                onChange={handleLanguageChange}
                onClear={handleLanguageChange}
                value={user.getSetting('lng', DefaultLanguage)}
              />
            )}
            <Tooltip
              position={'top-start'}
              title={t('Used to set the time zone of start and end dates for email campaigns')}
            >
              <TimezoneSelect label={t('Timezone')} value={user.get('timezone', 'UTC')} />
            </Tooltip>
          </Fieldset>
        </Card.Content>
        <Card.Content title={t('Booking link')}>
          <Input
            attribute={'calendly_link'}
            helpText={t('Add a link to your scheduling page, e.g. Calendly or Zoom.')}
            model={user}
            placeholder={'https://calendly.com/your-link'}
            type={'url'}
            validationError={flattenError(errors.calendly_link)}
          />
        </Card.Content>
      </Card>
    </Page>
  );
}

export default observer(ProfilePage);
